import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle, Send, Shield, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [focused, setFocused] = useState(false);

  const { settings, fetchPublicSettings } = useSystemSettingsStore();

  useEffect(() => { fetchPublicSettings(); }, [fetchPublicSettings]);
  useEffect(() => { setLogoError(false); }, [settings.logo_url]);

  const appName = settings.app_name || 'SIM-EVAL';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const readableFooterText = settings.footer_text || 'Copyright 2026 SIM-EVAL UBP Karawang. Hak cipta dilindungi.';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const footerText = settings.footer_text || '© 2026 SIM-EVAL UBP Karawang. All rights reserved.';
  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  useEffect(() => {
    if (appName) document.title = `Lupa Password - ${appName}`;
  }, [appName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Email wajib diisi'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Format email tidak valid'); return; }

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
      toast.success('Email reset password telah dikirim!');
    } catch (err: any) {
      if (err.response?.status === 422) {
        const emailError = err.response.data?.errors?.email?.[0];
        setError(emailError || 'Email tidak terdaftar');
      } else if (err.response?.status === 429) {
        setError('Terlalu banyak permintaan. Tunggu beberapa menit.');
      } else {
        setError(err.response?.data?.message || 'Terjadi kesalahan');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success('Email telah dikirim ulang!');
    } catch (err: any) {
      if (err.response?.status === 429) {
        toast.error('Tunggu beberapa menit sebelum mengirim ulang');
      } else {
        toast.error('Gagal mengirim ulang email');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#0f33ab] via-[#1a4fd6] to-[#0f33ab]" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0f33ab]/[0.02] rounded-full -mr-48 -mt-48" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0f33ab]/[0.03] rounded-full -ml-32 -mb-32" />

      <div className="relative z-10 w-full max-w-[440px] px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 overflow-hidden rounded-2xl ${showLogo ? 'bg-transparent' : 'bg-[#0f33ab] shadow-lg shadow-[#0f33ab]/30'
            }`}>
            {showLogo ? (
              <img src={logoUrl!} alt="Logo" className="w-full h-full object-contain" onError={() => setLogoError(true)} />
            ) : (
              <span className="text-2xl font-black text-white">{brandInitials}</span>
            )}
          </div>
          <h1 className="text-xl font-black text-[#0f33ab] tracking-tight">{appName}</h1>
          <p className="text-xs text-gray-500 mt-1">{institutionName}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0f33ab]/5 rounded-xl mb-3">
                  <Mail className="w-6 h-6 text-[#0f33ab]" />
                </div>
                <h2 className="text-xl font-black text-gray-900">Lupa Password?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Masukkan email Anda dan kami akan mengirimkan link reset password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Email
                  </label>
                  <div className={`relative rounded-xl transition-all duration-200 ${focused ? 'ring-2 ring-[#0f33ab]/20' : ''
                    }`}>
                    <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl transition-colors ${focused ? 'bg-[#0f33ab] text-white' : 'bg-gray-100 text-gray-400'
                      }`}>
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      placeholder="nama@ubpkarawang.ac.id"
                      className={`w-full pl-14 pr-4 py-3.5 rounded-xl border-2 text-sm font-medium bg-white focus:outline-none transition-all ${error
                          ? 'border-red-300'
                          : focused
                            ? 'border-[#0f33ab]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      autoFocus
                    />
                  </div>
                  {error && (
                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group transition-all duration-300 ${loading
                      ? 'bg-[#0f33ab]/70 cursor-not-allowed'
                      : 'bg-[#0f33ab] hover:shadow-lg hover:shadow-[#0f33ab]/30 active:scale-[0.98]'
                    }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Mengirim...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Kirim Link Reset
                      </>
                    )}
                  </span>
                </button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-black text-gray-900">Email Terkirim!</h2>
              <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                Link reset password telah dikirim ke{' '}
                <span className="font-semibold text-[#0f33ab]">{email}</span>.
                Silakan cek inbox atau folder spam.
              </p>

              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500">Tidak menerima email?</p>
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="text-sm font-semibold text-[#0f33ab] hover:underline mt-1 disabled:opacity-50"
                >
                  {loading ? 'Mengirim ulang...' : 'Kirim ulang email'}
                </button>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-700">
                  ⏰ Link berlaku selama 60 menit.
                </p>
              </div>
            </div>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f33ab] hover:text-[#0a2478] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke halaman login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Akses dilindungi koneksi terenkripsi</span>
          </div>
          <p className="text-xs text-gray-400">{readableFooterText}</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
